import React, { useState } from "react";
import { apiCall } from "../main/ApiCall";
import "../css/others/Popup.css";
import ToastPopup from "./ToastPopup";
import { EditFunctionCampaignPopupProps, createToastMessage, ToastMessage } from "../interFaces/interfaces";
const formatDateForBackend = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};
const formatDateForInput = (dateString: string) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};
const restrictedChars = /[\\:*?"<>|/]/;
const containsSpecialChars = (str: string) => {
  return restrictedChars.test(str);
};
const isWeekend = (date: Date) => {
  const day = date.getDay();
  return day === 0 || day === 6;
};
const isPastOrToday = (date: Date) => {
  const selected = new Date(date);
  selected.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return selected <= today;
};
const EditFunctionCampaignPopup: React.FC<EditFunctionCampaignPopupProps> = ({ campaign, onClose }) => {
  const [message, setMessage] = useState<ToastMessage | null>(null);
  const [updatedCampaign, setUpdatedCampaign] = useState({ ...campaign, campaignStartDate: formatDateForInput(campaign.campaignStartDate), campaignEndDate: formatDateForInput(campaign.campaignEndDate) });
  const updatedBy = localStorage.getItem("email");
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowDate = formatDateForInput(tomorrow.toString());
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (["auditObjective", "auditScope", "auditTeam"].includes(name)) {
      if (value.length > 60) {
        setMessage(createToastMessage("Maximum 60 characters allowed", "warning", 3000));
        return;
      }
      if (containsSpecialChars(value)) {
        setMessage(createToastMessage(`Special characters are not allowed - ${restrictedChars}`, "warning", 3000));
        return;
      }
    }
    if (name === "campaignStartDate") {
      const selectedDate = new Date(value);
      if (isPastOrToday(selectedDate)) {
        setMessage(createToastMessage("Past date and today are not allowed", "warning", 3000));
        return;
      }
      if (isWeekend(selectedDate)) {
        setMessage(createToastMessage("Saturday and Sunday are not allowed", "warning", 3000));
        return;
      }
      if (
        updatedCampaign.campaignEndDate &&
        new Date(updatedCampaign.campaignEndDate) < selectedDate
      ) {
        setUpdatedCampaign((prev: any) => ({ ...prev, [name]: value, campaignEndDate: "" }));
      } else {
        setUpdatedCampaign((prev: any) => ({ ...prev, [name]: value }));
      }
    } else if (name === "campaignEndDate") {
      if (!updatedCampaign.campaignStartDate) {
        setMessage(createToastMessage("Please select a start date first", "warning", 3000));
        return;
      }
      const selectedDate = new Date(value);
      if (isPastOrToday(selectedDate)) {
        setMessage(createToastMessage("Past date and today are not allowed", "warning", 3000));
        return;
      }
      if (isWeekend(selectedDate)) {
        setMessage(createToastMessage("Saturday and Sunday are not allowed", "warning", 3000));
        return;
      }
      if (selectedDate <= new Date(updatedCampaign.campaignStartDate)) {
        setMessage(createToastMessage("End date must be after start date", "warning", 3000));
        return;
      }
      setUpdatedCampaign((prev: any) => ({ ...prev, [name]: value }));
    } else {
      setUpdatedCampaign((prev: any) => ({ ...prev, [name]: value }));
    }
  };
  const handleSave = async () => {
    const textFields = ["auditObjective", "auditScope", "auditTeam"] as const;
    for (const field of textFields) {
      const fieldValue = updatedCampaign[field];
      if (typeof fieldValue === "string" && containsSpecialChars(fieldValue)) {
        const fieldLabel = field
          .replace(/([A-Z])/g, " $1")
          .replace(/^./, (str) => str.toUpperCase());
        setMessage(createToastMessage(`Special characters are not allowed in ${fieldLabel}`, "warning", 3000));
        return;
      }
    }
    if (!updatedCampaign.campaignStartDate || !updatedCampaign.campaignEndDate) {
      setMessage(createToastMessage("Please select both start and end dates", "warning", 3000));
      return;
    }
    const startDate = new Date(updatedCampaign.campaignStartDate);
    const endDate = new Date(updatedCampaign.campaignEndDate);
    if (isPastOrToday(startDate) || isPastOrToday(endDate)) {
      setMessage(createToastMessage("Past date and today are not allowed", "warning", 3000));
      return;
    }
    if (isWeekend(startDate) || isWeekend(endDate)) {
      setMessage(createToastMessage("Saturday and Sunday are not allowed", "warning", 3000));
      return;
    }
    if (endDate <= startDate) {
      setMessage(createToastMessage("End date must be after start date", "warning", 3000));
      return;
    }
    try {
      const updatedData = { ...updatedCampaign, updatedBy, updatedDate: formatDateForBackend(new Date()), campaignStartDate: formatDateForBackend(new Date(updatedCampaign.campaignStartDate)), campaignEndDate: formatDateForBackend(new Date(updatedCampaign.campaignEndDate)) };
      await apiCall("put", `/api/function/campaigns?campaignId=${campaign.campaignId}`, updatedData);
      setMessage(createToastMessage("Campaign updated successfully!", "success", 3000));
      setTimeout(() => window.location.reload(), 3000);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Failed to update campaign";
      setMessage(createToastMessage(errorMessage, "error", 3000));
      setTimeout(() => window.location.reload(), 3000);
    }
  };
  return (
    <div className="popup-overlay">
      <div className="modal-content1">
        <h3>Edit Function Campaign</h3>
        <div className="modal-content-list">
          <label>Function Audit Objective</label>
          <input type="text" name="auditObjective" value={updatedCampaign.auditObjective} onChange={handleChange} placeholder="Enter audit objective" />
          <label>Function Audit Scope</label>
          <input type="text" name="auditScope" value={updatedCampaign.auditScope} onChange={handleChange} placeholder="Enter audit scope" />
          <label>Function Audit Team</label>
          <input type="text" name="auditTeam" value={updatedCampaign.auditTeam} onChange={handleChange} placeholder="Enter audit team" />
          <label>Function Campaign Start Date</label>
          <input type="date" name="campaignStartDate" value={updatedCampaign.campaignStartDate} min={tomorrowDate} onChange={handleChange} />
          <label>Function Campaign End Date</label>
          <input type="date" name="campaignEndDate" value={updatedCampaign.campaignEndDate} min={updatedCampaign.campaignStartDate || tomorrowDate} onChange={handleChange} />
        </div>
        <div className="buttons">
          <button className="cancelbutton" onClick={onClose}>Cancel</button>
          <button className="savebutton" onClick={handleSave}>Save</button>
        </div>
      </div>
      {message && (<ToastPopup message={message.text} type={message.type} duration={message.duration} onClose={() => setMessage(null)} />)}
    </div>
  );
};
export default EditFunctionCampaignPopup;